var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import cx from 'classnames';
import { ELSIcon, ELSRadio } from 'components/common';
import SearchBar from 'components/common/search-bar/SearchBar';
import { KEY_CODES } from 'constants/app.constant';
import { getIndexByKeyCode, onKeyDownHandler } from 'helpers/ui.helper';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
var initialOptionIdx = -1;
var instanceCounter = 0;
var FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]';
// Finds the next focusable element after `widgetRoot` in document order, without enumerating every
// focusable element in `root` first (as a `root.querySelectorAll(...)` scan would) -- on pages with
// large tables that's an expensive full-page scan on every Tab press. A TreeWalker lets us skip
// `widgetRoot`'s own subtree entirely (the popup that's about to unmount) and stop at the very first
// match, so cost scales with distance to the next focusable element, not with total page size.
var findNextFocusableAfter = function (widgetRoot, root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
        acceptNode: function (node) { return (widgetRoot.contains(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT); }
    });
    walker.currentNode = widgetRoot;
    var node = walker.nextNode();
    while (node) {
        if (node instanceof HTMLElement && node.matches(FOCUSABLE_SELECTOR) && node.offsetParent !== null) {
            return node;
        }
        node = walker.nextNode();
    }
    return null;
};
var RadioMenu = function (_a) {
    var _b;
    var options = _a.options, checkedKey = _a.checkedKey, inline = _a.inline, disabled = _a.disabled, hideRadio = _a.hideRadio, searchable = _a.searchable, searchPlaceholder = _a.searchPlaceholder, expandWidth = _a.expandWidth, renderItem = _a.renderItem, renderDisplayItem = _a.renderDisplayItem, _c = _a.activeItemClass, activeItemClass = _c === void 0 ? '' : _c, _d = _a.itemClass, itemClass = _d === void 0 ? '' : _d, _e = _a.className, className = _e === void 0 ? '' : _e, _f = _a.listClass, listClass = _f === void 0 ? '' : _f, ariaLabel = _a.ariaLabel, onOptionChange = _a.onOptionChange;
    var menuRef = useRef(null);
    var triggerRef = useRef(null);
    var selectedOptionRef = useRef(null);
    var focusRestoreTimeoutRef = useRef(null);
    // Lazy useState initializer (not useRef) so the module-level counter is only bumped once per
    // mount, matching the class version's one-time class-field initialization -- a plain useRef
    // argument would re-evaluate (and re-increment the counter) on every render.
    var listboxId = useState(function () {
        instanceCounter += 1;
        return "radio-menu-listbox-".concat(instanceCounter);
    })[0];
    var _g = useState(false), opened = _g[0], setOpened = _g[1];
    var _h = useState(initialOptionIdx), optionIdxSelectedByKey = _h[0], setOptionIdxSelectedByKey = _h[1];
    var _j = useState(false), shouldChangeOptionListPosition = _j[0], setShouldChangeOptionListPosition = _j[1];
    var _k = useState(''), searchText = _k[0], setSearchText = _k[1];
    var _l = useState(checkedKey), tempCheckedKey = _l[0], setTempCheckedKey = _l[1];
    // These three are registered as long-lived window listeners (or depended on by one), so they're
    // memoized with stable references -- otherwise the mount effect below would tear down and
    // re-subscribe on every render instead of once per mount, like the class version's
    // componentDidMount/componentWillUnmount pair.
    var hideOptions = useCallback(function () {
        setOpened(false);
        setOptionIdxSelectedByKey(initialOptionIdx);
        setSearchText('');
    }, []);
    var changeOptionListPosition = useCallback(function () {
        // Guards against shallow-rendering test environments where the ref never attaches to a real
        // DOM node (this callback is also called directly, not just from the resize listener) -- in
        // the browser the ref is always set by the time this can fire, so this is a no-op there.
        if (!menuRef.current) {
            return;
        }
        var optionBoxBoundingClientRect = menuRef.current.getBoundingClientRect();
        setShouldChangeOptionListPosition(optionBoxBoundingClientRect.x > window.innerWidth / 2);
    }, []);
    var handleClickOutside = useCallback(function (evt) {
        if (menuRef.current && !menuRef.current.contains(evt.target)) {
            hideOptions();
        }
    }, [hideOptions]);
    useEffect(function () {
        window.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('resize', changeOptionListPosition);
        return function () {
            window.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', changeOptionListPosition);
        };
    }, [handleClickOutside, changeOptionListPosition]);
    // The forward-Tab handler below schedules a setTimeout to restore focus after this component's
    // own popup unmounts; a plain setTimeout isn't tied to the component lifecycle, so if this
    // component itself unmounts (e.g. the caller navigates away) before that 0ms delay elapses, the
    // callback would still fire against refs React has already cleared. Cancelling it on unmount
    // avoids that stale-ref call entirely, rather than relying on optional chaining to no-op safely.
    useEffect(function () { return function () {
        if (focusRestoreTimeoutRef.current) {
            clearTimeout(focusRestoreTimeoutRef.current);
        }
    }; }, []);
    // Runs whenever `opened` changes (mirroring the class's componentDidUpdate check that compared
    // prevState.opened to state.opened); the `if (opened)` guard means the initial mount pass (where
    // opened starts false) is a no-op here too, exactly like componentDidUpdate never firing on mount.
    useEffect(function () {
        var _a;
        if (opened) {
            changeOptionListPosition();
            (_a = selectedOptionRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ block: 'nearest' });
        }
    }, [opened, changeOptionListPosition]);
    // Faithfully replicates the class version's componentDidUpdate check, which compared
    // prevState.tempCheckedKey to props.checkedKey on EVERY update (not just when checkedKey itself
    // changed): if a caller doesn't act on onOptionChange, the very next unrelated re-render would
    // revert any uncommitted internal selection back to the external checkedKey prop. A simpler
    // `useEffect(() => setTempCheckedKey(checkedKey), [checkedKey])` would only resync when checkedKey
    // itself changes and would NOT revert in that scenario, which is a real (if obscure) behavior
    // difference -- so this uses a ref to snapshot "prevState.tempCheckedKey" and runs on every render.
    var prevTempCheckedKeyRef = useRef(tempCheckedKey);
    // The missing dependency array is intentional (see above), not an oversight -- adding
    // [checkedKey, tempCheckedKey] as suggested would only re-run this when one of those two values
    // itself changes, which is exactly the narrower behavior this is deliberately avoiding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(function () {
        if (prevTempCheckedKeyRef.current !== checkedKey) {
            setTempCheckedKey(checkedKey);
            prevTempCheckedKeyRef.current = checkedKey;
        }
        else {
            prevTempCheckedKeyRef.current = tempCheckedKey;
        }
    });
    var toggleOptions = function () {
        if (!disabled) {
            setOpened(function (prevOpened) { return !prevOpened; });
            setOptionIdxSelectedByKey(initialOptionIdx);
            setSearchText('');
        }
    };
    var handleSearch = function (evt) {
        setSearchText(evt.target.value);
    };
    var handleOptionChange = function (option) {
        setTempCheckedKey(option.key);
        onOptionChange(option.key);
    };
    var openOptionByKeyboard = function () {
        setOpened(true);
        setOptionIdxSelectedByKey(0);
        setSearchText('');
    };
    var applyDataWhenHideRadioModeByKeyCode = function () {
        if (optionIdxSelectedByKey !== initialOptionIdx) {
            var newCheckedKey = options[optionIdxSelectedByKey].key;
            setTempCheckedKey(newCheckedKey);
            onOptionChange(newCheckedKey);
        }
        toggleOptions();
    };
    // radio =>  SPACE: Choose option, ENTER: Apply the option
    // hide radio ==> SPACE & ENTER: Choose and apply option
    var onSpaceKeyOpenMenu = function (currentOptionIdxSelectedByKey) {
        if (currentOptionIdxSelectedByKey !== initialOptionIdx) {
            setTempCheckedKey(options[currentOptionIdxSelectedByKey].key);
        }
    };
    var onEnterKeyOpenMenu = function (currentOptionIdxSelectedByKey) {
        if (currentOptionIdxSelectedByKey !== initialOptionIdx) {
            onOptionChange(tempCheckedKey);
        }
        toggleOptions();
    };
    var handleMenuKeyDown = function (evt) {
        var key = evt.keyCode;
        if (key === KEY_CODES.TAB) {
            if (opened) {
                hideOptions();
            }
            return;
        }
        evt.preventDefault();
        var max = options.length - 1;
        switch (key) {
            case KEY_CODES.ENTER: {
                if (hideRadio && opened) {
                    applyDataWhenHideRadioModeByKeyCode();
                    break;
                }
                if (opened) {
                    onEnterKeyOpenMenu(optionIdxSelectedByKey);
                }
                else {
                    openOptionByKeyboard();
                }
                break;
            }
            case KEY_CODES.SPACE: {
                if (hideRadio && opened) {
                    applyDataWhenHideRadioModeByKeyCode();
                    break;
                }
                if (opened) {
                    onSpaceKeyOpenMenu(optionIdxSelectedByKey);
                }
                else {
                    openOptionByKeyboard();
                }
                break;
            }
            case KEY_CODES.DOWN:
            case KEY_CODES.UP: {
                setOptionIdxSelectedByKey(function (prevOptionIdxSelectedByKey) { return getIndexByKeyCode(key, prevOptionIdxSelectedByKey, max); });
                break;
            }
            case KEY_CODES.ESC: {
                hideOptions();
                setTempCheckedKey(checkedKey);
                break;
            }
            default: {
                break;
            }
        }
    };
    // The search input (when `searchable`) is a DOM sibling of the trigger, not a descendant of it,
    // so `handleMenuKeyDown` (only attached to the trigger) never fires for keys pressed while
    // focus is inside it. Previously that meant: (1) nothing moved focus into the search box in the
    // first place, and (2) even if focus somehow landed there, Tab/Arrow/Enter/Escape did nothing --
    // the box is auto-focused on open (see the SearchBar element below) to fix (1); this handler,
    // wired to SearchBar's keyDownHandler, fixes (2) by reusing the same option-navigation logic,
    // while deliberately leaving Space and all other printable keys untouched so users can still type
    // multi-word search terms.
    var handleSearchKeyDown = function (evt) {
        var _a, _b, _c, _d;
        var key = evt.keyCode;
        var max = options.length - 1;
        switch (key) {
            case KEY_CODES.ENTER: {
                evt.preventDefault();
                if (hideRadio) {
                    applyDataWhenHideRadioModeByKeyCode();
                }
                else {
                    onEnterKeyOpenMenu(optionIdxSelectedByKey);
                }
                (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                break;
            }
            case KEY_CODES.DOWN:
            case KEY_CODES.UP: {
                evt.preventDefault();
                setOptionIdxSelectedByKey(function (prevOptionIdxSelectedByKey) { return getIndexByKeyCode(key, prevOptionIdxSelectedByKey, max); });
                break;
            }
            case KEY_CODES.ESC: {
                evt.preventDefault();
                hideOptions();
                setTempCheckedKey(checkedKey);
                (_b = triggerRef.current) === null || _b === void 0 ? void 0 : _b.focus();
                break;
            }
            case KEY_CODES.TAB: {
                if (evt.shiftKey) {
                    // The trigger is always the immediately-preceding stop, so no need to search the page.
                    evt.preventDefault();
                    hideOptions();
                    (_c = triggerRef.current) === null || _c === void 0 ? void 0 : _c.focus();
                    break;
                }
                // Forward Tab: figure out where focus should land on the page BEFORE the search box (and
                // the rest of the popup) unmounts, then move it there once the DOM has updated -- mirrors
                // CheckboxMenu's handleMenuKeyDown TAB handling for the same underlying problem.
                evt.preventDefault();
                var menuRoot = menuRef.current;
                var root = ((_d = menuRoot === null || menuRoot === void 0 ? void 0 : menuRoot.closest('[role="dialog"]')) !== null && _d !== void 0 ? _d : document.body);
                var nextFocusable_1 = menuRoot ? findNextFocusableAfter(menuRoot, root) : null;
                hideOptions();
                focusRestoreTimeoutRef.current = setTimeout(function () {
                    var _a;
                    focusRestoreTimeoutRef.current = null;
                    (_a = (nextFocusable_1 !== null && nextFocusable_1 !== void 0 ? nextFocusable_1 : triggerRef.current)) === null || _a === void 0 ? void 0 : _a.focus();
                }, 0);
                break;
            }
            default: {
                break;
            }
        }
    };
    var renderDisplayName = function (selectedOption) {
        if (renderDisplayItem) {
            return renderDisplayItem(__assign({}, selectedOption));
        }
        return selectedOption.displayName || selectedOption.name;
    };
    // Memoized so a large `options` list isn't re-scanned on renders that don't touch these inputs --
    // e.g. arrow-key navigation only changes `optionIdxSelectedByKey`, which used to still trigger a
    // full re-filter/re-find of `options` on every keypress even though neither `options` nor
    // `checkedKey`/`searchText` changed.
    var selectedOption = useMemo(function () { return options.find(function (option) { return option.key === checkedKey; }) || {}; }, [options, checkedKey]);
    var displayOptions = useMemo(function () { return options.filter(function (option) { return option.name.toLowerCase().includes(searchText.toLowerCase()); }); }, [options, searchText]);
    var activeDescendant = opened && optionIdxSelectedByKey !== initialOptionIdx ? "radio-menu-option-".concat((_b = options[optionIdxSelectedByKey]) === null || _b === void 0 ? void 0 : _b.key) : undefined;
    return (<div ref={menuRef} className={cx({ 'u-els-display-inline-block': inline }, 'c-select-menu c-radio-menu', { 'c-select-menu--disabled': disabled }, className)}>
      <div ref={triggerRef} role="combobox" tabIndex={0} aria-haspopup="listbox" aria-expanded={opened} aria-controls={opened ? listboxId : undefined} aria-activedescendant={activeDescendant} aria-label={ariaLabel ? "".concat(ariaLabel, ", ").concat(selectedOption.name || '') : undefined} className={cx({ 'u-els-display-inline-block': inline, 'c-select-menu--opened': opened }, 'c-select-menu__trigger')} onClick={toggleOptions} onKeyDown={handleMenuKeyDown}>
        <span title={selectedOption.name} className="c-select-menu__display-label">
          {renderDisplayName(selectedOption)}
        </span>
        <ELSIcon name="chevron-down" size="1x" customClass="u-els-margin-left-1o2 c-select-menu__rotate-arrow"/>
      </div>

      {opened && (<div className={cx('c-select-menu__options', {
                'c-select-menu__options--position-changed': shouldChangeOptionListPosition,
                'c-select-menu__options--expand-width': expandWidth
            }, listClass)}>
          <div className="c-select-menu__options-wrapper">
            {searchable && (<SearchBar placeholder={searchPlaceholder} value={searchText} handleSearch={handleSearch} keyDownHandler={handleSearchKeyDown} autoFocus 
            // The search input is auto-focused as soon as it renders (see above), so it -- not
            // the trigger -- is the element a screen reader treats as "focused" while the menu
            // is open with search active. aria-activedescendant is only meaningful on whichever
            // element currently has DOM focus, so it (and the rest of the combobox state) needs
            // to be projected here too, or the highlighted option would never be announced while
            // arrow-navigating from the search box.
            ariaAttrs={{
                    role: 'combobox',
                    'aria-haspopup': 'listbox',
                    'aria-expanded': true,
                    'aria-controls': listboxId,
                    'aria-activedescendant': activeDescendant,
                    'aria-autocomplete': 'list'
                }}/>)}
            <ul id={listboxId} className="c-select-menu__options-list" role="listbox" aria-label={ariaLabel}>
              {displayOptions.map(function (option, idx) {
                var _a;
                var changeHandler = function (evt) {
                    evt.preventDefault();
                    evt.stopPropagation();
                    handleOptionChange(option);
                    hideOptions();
                };
                var name = option.name, key = option.key;
                return (<li ref={key === checkedKey ? selectedOptionRef : null} role="option" id={"radio-menu-option-".concat(key)} aria-selected={key === checkedKey} title={name} key={"option-".concat(key)} className={cx('o-els-flex-layout__item--grow-1 c-select-menu__option', {
                        'c-select-menu__option--selected-by-key': optionIdxSelectedByKey === idx
                    }, itemClass)}>
                    {hideRadio ? (<div role="button" tabIndex={-1} className={cx({ 'u-els-text-decoration-underline u-els-color-secondary': key === tempCheckedKey && optionIdxSelectedByKey !== idx }, (_a = {}, _a["".concat(activeItemClass)] = key === tempCheckedKey, _a))} onClick={changeHandler} onKeyDown={function (evt) { return onKeyDownHandler(evt, changeHandler); }}>
                        {renderItem ? renderItem(__assign({}, option)) : name}
                      </div>) : (<ELSRadio name={name} value={key} checked={key === tempCheckedKey} changeHandler={changeHandler}>
                        {renderItem ? renderItem(__assign({}, option)) : name}
                      </ELSRadio>)}
                  </li>);
            })}
            </ul>
          </div>
        </div>)}
    </div>);
};
export default RadioMenu;
